/**
 * AssuranceDrawer — Decision support, not data dump.
 *
 * Slide-in panel with 5-layer fixed narrative:
 *   Layer 1: Summary sentence (always visible)
 *   Layer 2: Status cards (issues expanded, healthy collapsed)
 *   Layer 3: Issues requiring attention
 *   Layer 4: Evidence details (expandable, for auditors)
 *   Layer 5: Actions (copy, export, open run, view receipts, share)
 *
 * Motion: 280ms translateX slide from right. Restrained.
 */

import type {
  ActionAssuranceData,
  AssuranceCardName,
  AssuranceIssue,
  SplitActorRecord,
  DelegationSnapshot,
  ExposureSummary,
  AuthorizationMoment,
} from './action-assurance-types';
import { CARD_LABELS, assuranceStatusColor } from './action-assurance-types';
import { ActionAssuranceBadge } from './ActionAssuranceBadge';
import { AuthorizationTimeline } from './AuthorizationTimeline';
import { ExposureGauge } from './ExposureGauge';

const React = (globalThis as any).React;
const { useState, useEffect, useCallback, useRef } = React;

export interface AssuranceDrawerProps {
  open: boolean;
  onClose: () => void;
  assurance: ActionAssuranceData;
  splitActor?: SplitActorRecord | null;
  delegation?: DelegationSnapshot | null;
  exposure?: ExposureSummary | null;
  authorizationMoments?: Record<string, AuthorizationMoment>;
  runId?: string;
  plugin?: string;
  onOpenRun?: (runId: string) => void;
  onExportEvidence?: () => void;
}

const CARD_ORDER: AssuranceCardName[] = [
  'identity', 'intent', 'delegation', 'authorization',
  'exposure', 'tool_integrity', 'proof',
];

function StatusDot({ status }: { status: 'ok' | 'issue' | 'missing' }) {
  const colors = {
    ok: 'var(--color-success, #52c41a)',
    issue: 'var(--color-warning, #faad14)',
    missing: 'var(--color-text-disabled, #bfbfbf)',
  };
  return React.createElement('span', {
    style: {
      width: 8, height: 8, borderRadius: '50%',
      backgroundColor: colors[status], flexShrink: 0,
    },
  });
}

export function AssuranceDrawer({
  open,
  onClose,
  assurance,
  splitActor,
  delegation,
  exposure,
  authorizationMoments,
  runId,
  plugin,
  onOpenRun,
  onExportEvidence,
}: AssuranceDrawerProps) {
  const [visible, setVisible] = useState(false);
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const drawerRef = useRef(null as HTMLDivElement | null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  const handleKeyDown = useCallback((e: any) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  const testIdPrefix = plugin ? `${plugin}-` : '';
  const issues = assurance.issues || [];
  const failedIssues = issues.filter((i: AssuranceIssue) => i.severity === 'failed');
  const reviewIssues = issues.filter((i: AssuranceIssue) => i.severity === 'needs_review');
  const allIssues = [...failedIssues, ...reviewIssues];

  // Determine which cards have issues
  const cardsWithIssues = new Set(issues.map((i: AssuranceIssue) => i.card));

  const overlayStyle: Record<string, string | number> = {
    position: 'fixed',
    inset: 0,
    zIndex: 1000,
    display: 'flex',
    justifyContent: 'flex-end',
  };

  const backdropStyle: Record<string, string | number> = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    opacity: visible ? 1 : 0,
    transition: 'opacity 200ms ease-out',
  };

  const panelStyle: Record<string, string | number> = {
    position: 'relative',
    width: 420,
    maxWidth: '100vw',
    height: '100%',
    backgroundColor: 'var(--color-bg-elevated, #fff)',
    boxShadow: 'var(--shadow-lg, -4px 0 24px rgba(0,0,0,0.12))',
    overflowY: 'auto',
    transform: visible ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1)',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: Record<string, string | number> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid var(--color-border, #f0f0f0)',
  };

  const sectionStyle: Record<string, string | number> = {
    padding: '12px 20px',
    borderBottom: '1px solid var(--color-border-subtle, #f5f5f5)',
  };

  return React.createElement('div', {
    'data-testid': `${testIdPrefix}assurance-drawer`,
    style: overlayStyle,
    ref: drawerRef,
  },
    React.createElement('div', { style: backdropStyle, onClick: onClose }),
    React.createElement('div', { style: panelStyle, role: 'dialog', 'aria-label': 'Action Assurance' },
      // Header
      React.createElement('div', { style: headerStyle },
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement(ActionAssuranceBadge, {
            status: assurance.status as any,
            reason: assurance.reason,
            size: 'md',
            context: 'run_detail',
          }),
          React.createElement('span', {
            style: { fontSize: 16, fontWeight: 600, color: 'var(--color-text-primary)' },
          }, 'Action Assurance'),
        ),
        React.createElement('button', {
          onClick: onClose,
          'aria-label': 'Close drawer',
          style: {
            border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 18, color: 'var(--color-text-secondary)', padding: 4,
          },
        }, '×'),
      ),

      // Layer 1: Summary
      React.createElement('div', {
        'data-testid': `${testIdPrefix}assurance-summary`,
        style: { ...sectionStyle, backgroundColor: 'var(--color-bg-subtle, rgba(0,0,0,0.02))' },
      },
        React.createElement('p', {
          style: { margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--color-text-primary)' },
        }, assurance.summary_sentence || assurance.reason),
      ),

      // Layer 2: Status Cards
      React.createElement('div', { style: { padding: '8px 20px' } },
        ...CARD_ORDER.map((cardName) => {
          const hasIssue = cardsWithIssues.has(cardName);
          const cardIssues = issues.filter((i: AssuranceIssue) => i.card === cardName);
          const lawKey = Object.keys(assurance.law_coverage || {}).find(
            (k) => k.includes(cardName) || CARD_TO_LAW[cardName] === k,
          );
          const lawStatus = lawKey ? assurance.law_coverage[lawKey]?.status : undefined;

          const dotStatus = hasIssue ? 'issue' : (lawStatus === 'complete' || lawStatus === 'n/a') ? 'ok' : 'missing';

          // Actor chain inline for identity card
          const actorChainEl = cardName === 'identity' && splitActor
            ? React.createElement('div', {
                style: { marginTop: 4, paddingLeft: 16, fontSize: 12, color: 'var(--color-text-secondary)' },
                'data-testid': `${testIdPrefix}assurance-actor-chain`,
              },
                [splitActor.delegator, splitActor.agent, splitActor.worker, splitActor.downstream]
                  .filter(Boolean).join(' \u2192 ') || 'Single actor',
              )
            : null;

          return React.createElement('div', {
            key: cardName,
            'data-testid': `${testIdPrefix}assurance-card-${cardName}`,
            style: {
              padding: '8px 0',
              borderBottom: '1px solid var(--color-border-subtle, #fafafa)',
            },
          },
            React.createElement('div', {
              style: { display: 'flex', alignItems: 'center', gap: 8 },
            },
              React.createElement(StatusDot, { status: dotStatus }),
              React.createElement('span', {
                style: { fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary)' },
              }, CARD_LABELS[cardName]),
              !hasIssue && React.createElement('span', {
                style: { fontSize: 11, color: 'var(--color-text-disabled)', marginLeft: 'auto' },
              }, lawStatus === 'n/a' ? 'N/A' : 'Complete'),
            ),
            actorChainEl,
            hasIssue && React.createElement('div', {
              style: { marginTop: 4, paddingLeft: 16 },
            },
              ...cardIssues.map((issue: AssuranceIssue, idx: number) =>
                React.createElement('div', {
                  key: idx,
                  style: { fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.4 },
                }, issue.summary),
              ),
            ),
          );
        }),
      ),

      // Layer 3: Issues Requiring Attention
      allIssues.length > 0 && React.createElement('div', { style: sectionStyle },
        React.createElement('h4', {
          style: { margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' },
        }, 'Issues requiring attention'),
        ...allIssues.map((issue: AssuranceIssue, idx: number) =>
          React.createElement('div', {
            key: idx,
            style: {
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '6px 0', fontSize: 12, color: 'var(--color-text-secondary)',
            },
          },
            React.createElement('span', {
              style: {
                width: 6, height: 6, borderRadius: '50%', marginTop: 5, flexShrink: 0,
                backgroundColor: issue.severity === 'failed'
                  ? 'var(--color-error)' : 'var(--color-warning)',
              },
            }),
            React.createElement('span', null, issue.summary),
          ),
        ),
      ),

      // Authorization Timeline (if data present)
      authorizationMoments && Object.keys(authorizationMoments).length > 0 &&
        React.createElement('div', { style: sectionStyle },
          React.createElement(AuthorizationTimeline, { moments: authorizationMoments, plugin }),
        ),

      // Exposure Gauge (if data present)
      exposure && React.createElement('div', { style: sectionStyle },
        React.createElement(ExposureGauge, { exposure, plugin }),
      ),

      // Layer 4: Evidence Details (expandable)
      React.createElement('div', { style: sectionStyle },
        React.createElement('button', {
          onClick: () => setEvidenceExpanded((e: boolean) => !e),
          style: {
            display: 'flex', alignItems: 'center', gap: 6,
            border: 'none', background: 'none', cursor: 'pointer',
            fontSize: 12, color: 'var(--color-text-secondary)', padding: 0,
            fontFamily: 'inherit',
          },
          'aria-expanded': evidenceExpanded,
        },
          React.createElement('span', null, evidenceExpanded ? '▾' : '▸'),
          React.createElement('span', null, 'Evidence details'),
        ),
        evidenceExpanded && React.createElement('pre', {
          style: {
            marginTop: 8, padding: 12,
            backgroundColor: 'var(--color-bg-subtle)',
            borderRadius: 'var(--border-radius-sm, 4px)',
            fontSize: 11, lineHeight: 1.4, overflow: 'auto',
            maxHeight: 300, color: 'var(--color-text-secondary)',
          },
        }, JSON.stringify(assurance.law_coverage, null, 2)),
      ),

      // Layer 5: Actions
      React.createElement('div', {
        style: { padding: '16px 20px', marginTop: 'auto', borderTop: '1px solid var(--color-border)' },
      },
        React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } },
          React.createElement('button', {
            onClick: () => {
              const text = assurance.summary_sentence || assurance.reason;
              navigator.clipboard?.writeText(text);
            },
            style: actionBtnStyle,
          }, 'Copy summary'),
          onExportEvidence && React.createElement('button', {
            onClick: onExportEvidence,
            style: actionBtnStyle,
          }, 'Export evidence'),
          runId && onOpenRun && React.createElement('button', {
            onClick: () => onOpenRun(runId),
            style: actionBtnStyle,
          }, 'View full run'),
        ),
      ),
    ),
  );
}

const actionBtnStyle: Record<string, string | number> = {
  padding: '6px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--border-radius-sm, 4px)',
  backgroundColor: 'var(--color-bg-elevated, #fff)',
  color: 'var(--color-text-primary)',
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const CARD_TO_LAW: Record<AssuranceCardName, string> = {
  identity: 'law_1_split_actor',
  intent: 'law_2_generated_intent',
  delegation: 'law_3_constrained_delegation',
  authorization: 'law_4_continuous_authorization',
  exposure: 'law_5_minimal_exposure',
  tool_integrity: 'law_6_governed_tool_integrity',
  proof: 'law_7_proof_carrying_action',
};
